/**
 * Validate Overlay — diagrammatic boundary rings.
 *
 * When the field transitions to Validate mode, clusters collapse
 * into proportional circles. These rings define those circles —
 * clean, measured, precise.
 *
 * McCandless Water World lineage: each bubble has a measured boundary.
 * The rings say: "this is a unit of meaning, and here is its edge."
 *
 * Invisible in Sense mode. Appear only when the field earns it.
 */

import * as THREE from 'three';
import type { SyntheticField, SyntheticNode } from '../data/synthetic';
import { useAuraStore } from '../state/store';

const RING_SEGMENTS = 64;

interface ClusterRing {
  line: THREE.Line;
  radius: number;
  center: THREE.Vector3;
  color: THREE.Color;
  intensity: number;
  category: string;
}

export class ValidateOverlay {
  public group: THREE.Group;
  private rings: ClusterRing[] = [];
  private material: THREE.ShaderMaterial;

  constructor(field: SyntheticField) {
    this.group = new THREE.Group();

    // Shared shader — thin precise lines that fade in/out with validate transition
    this.material = new THREE.ShaderMaterial({
      vertexShader: /* glsl */ `
        attribute float aOpacity;
        attribute vec3 aColor;
        varying float vOpacity;
        varying vec3 vColor;

        void main() {
          vOpacity = aOpacity;
          vColor = aColor;
          gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
      `,
      fragmentShader: /* glsl */ `
        uniform float u_reveal;      // 0→1 validate transition
        uniform float u_entryProgress;
        varying float vOpacity;
        varying vec3 vColor;

        void main() {
          float alpha = vOpacity * u_reveal * u_entryProgress;
          if (alpha < 0.005) discard;

          // Slightly brighter toward full reveal for crispness
          vec3 col = vColor * (0.8 + u_reveal * 0.4);
          gl_FragColor = vec4(col, alpha);
        }
      `,
      uniforms: {
        u_reveal: { value: 0 },
        u_entryProgress: { value: 0 },
      },
      transparent: true,
      depthWrite: false,
      blending: THREE.NormalBlending,
    });

    this.buildRings(field);
  }

  private buildRings(field: SyntheticField) {
    // Extract main clusters (same filter as synthetic data: id starts with 'cluster-')
    const mainClusters: SyntheticNode[] = [];
    for (const node of field.nodes) {
      if (node.id.startsWith('cluster-')) {
        mainClusters.push(node);
      }
    }

    for (const cluster of mainClusters) {
      // Radius matches the shader collapse formula: 8 + weight * 40
      const radius = 8 + cluster.intensity * 40;
      const center = new THREE.Vector3(...cluster.position);
      const color = new THREE.Color(cluster.color[0], cluster.color[1], cluster.color[2]);

      // Brighten for diagram visibility (same approach as labels)
      const brightColor = new THREE.Color(
        Math.min(1, color.r * 1.3 + 0.15),
        Math.min(1, color.g * 1.3 + 0.15),
        Math.min(1, color.b * 1.3 + 0.15),
      );

      const ring = this.createRing(center, radius, brightColor, cluster.intensity);

      this.rings.push({
        line: ring,
        radius,
        center,
        color: brightColor,
        intensity: cluster.intensity,
        category: cluster.category,
      });

      this.group.add(ring);
    }
  }

  /**
   * Create a single circle line at a cluster center.
   * Ring lies in the XY plane at the cluster's position (camera-facing enough
   * for the overhead-ish default perspective).
   *
   * For a spatial field, we use three overlapping rings at slight tilts
   * so the boundary reads from any camera angle — a "sphere outline" effect.
   */
  private createRing(
    center: THREE.Vector3,
    radius: number,
    color: THREE.Color,
    intensity: number,
  ): THREE.Line {
    // Single ring in XZ plane (most visible from default camera looking down Z)
    const points: THREE.Vector3[] = [];
    for (let i = 0; i <= RING_SEGMENTS; i++) {
      const angle = (i / RING_SEGMENTS) * Math.PI * 2;
      points.push(new THREE.Vector3(
        center.x + Math.cos(angle) * radius,
        center.y + Math.sin(angle) * radius * 0.3,  // Slightly elliptical for spatial depth
        center.z + Math.sin(angle) * radius,
      ));
    }

    const positions = new Float32Array(points.length * 3);
    const opacities = new Float32Array(points.length);
    const colors = new Float32Array(points.length * 3);

    for (let i = 0; i < points.length; i++) {
      positions[i * 3] = points[i].x;
      positions[i * 3 + 1] = points[i].y;
      positions[i * 3 + 2] = points[i].z;

      // Base opacity scales with intensity — stronger clusters have sharper rings
      opacities[i] = 0.15 + intensity * 0.35;

      colors[i * 3] = color.r;
      colors[i * 3 + 1] = color.g;
      colors[i * 3 + 2] = color.b;
    }

    const geometry = new THREE.BufferGeometry();
    geometry.setAttribute('position', new THREE.BufferAttribute(positions, 3));
    geometry.setAttribute('aOpacity', new THREE.BufferAttribute(opacities, 1));
    geometry.setAttribute('aColor', new THREE.BufferAttribute(colors, 3));

    const line = new THREE.Line(geometry, this.material);
    line.frustumCulled = false;
    return line;
  }

  /**
   * Per-frame update.
   * Rings appear only in Validate mode — the diagram's measured boundaries.
   */
  update(entryProgress: number) {
    const { mode, modeTransitionProgress } = useAuraStore.getState();

    // Target: 1 in validate, 0 otherwise
    const target = mode === 'validate' ? modeTransitionProgress : 0;
    const current = this.material.uniforms.u_reveal.value as number;

    // Smooth transition (slightly faster than tendrils — rings should lead)
    this.material.uniforms.u_reveal.value = current + (target - current) * 0.08;
    this.material.uniforms.u_entryProgress.value = entryProgress;
  }

  dispose() {
    for (const ring of this.rings) {
      ring.line.geometry.dispose();
    }
    this.material.dispose();
  }
}
